<template>
  <div class="chart-wrapper">
    <!-- 凡例 -->
    <div class="legend-container">
      <div class="legend-item" v-for="(color, category) in colorMapping" :key="category">
        <div class="legend-color" :style="{ backgroundColor: color }"></div>
        <span>{{ category }}</span>
      </div>
    </div>

    <!-- チャート -->
    <div class="chart-container" :style="{ height: props.data.length * 60 + 45 + 'px' }">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, watch, computed, onBeforeUnmount } from 'vue'
  import Chart from 'chart.js/auto'
  import { data } from 'autoprefixer'

  // Props定義
  const props = defineProps({
    // チャートデータ - オブジェクトまたは配列
    data: {
      type: [Object, Array],
      required: true,
      // オブジェクト例: { '開沼': { 案件: 3, 電子申請: 1, 助成金: 4, '36申請': 2 }, ... }
      // 配列例: [{ name: '開沼', 案件: 3, 電子申請: 1, ... }, { name: '開沼', ... }]
    },
    // カラーマッピング（カテゴリ:色）
    colorMapping: {
      type: Object,
      default: () => ({
        案件: '#4285F4', // 青
        電子申請: '#EA4335', // 赤
        助成金: '#FBBC05', // 黄
        '36協定': '#34A853', // 緑
      }),
    },
    // 最小スケール（これより小さくならない）
    minScale: {
      type: Number,
      default: 10,
    },
    // チャートの高さ
    height: {
      type: String,
      default: '350px',
    },
  })

  // テンプレート参照
  const chartCanvas = ref(null)
  // チャートインスタンス
  let chart = null

  // データ処理（配列またはオブジェクト形式をサポート）
  const processedData = computed(() => {
    // データが配列の場合は処理する（同姓同名は個別に保持）
    if (Array.isArray(props.data)) {
      // 結果を格納するオブジェクト
      const result = {}

      props.data.forEach((item, index) => {
        // nameとindexを組み合わせて一意のキーを作成（同姓同名を区別するため）
        const name = item.name || '不明'
        // displayNameはチャート表示用、keyはオブジェクトのキー
        const displayName = name
        const key = `${name}_${index}`

        // 新しいエントリを初期化
        result[key] = {
          _displayName: displayName, // 表示用の名前を保存
          ...Object.keys(props.colorMapping).reduce((acc, category) => {
            acc[category] = item[category] !== undefined ? item[category] : 0
            return acc
          }, {}),
        }
      })

      return result
    }

    // オブジェクト形式の場合はそのまま返す
    return props.data
  })

  // データ処理用の計算プロパティ
  const chartData = computed(() => {
    const data = processedData.value
    const keys = Object.keys(data)

    // 表示用のラベルを取得（_displayNameがあればそれを使用、なければキーをそのまま使用）
    const labels = keys.map(key => {
      return data[key]._displayName !== undefined ? data[key]._displayName : key
    })

    const categories = Object.keys(props.colorMapping)

    const datasets = categories.map(category => {
      return {
        label: category,
        data: keys.map(key => {
          // _displayNameはデータプロパティではないのでスキップ
          if (category === '_displayName') return 0
          return data[key][category] || 0
        }),
        backgroundColor: props.colorMapping[category],
      }
    })

    return {
      labels,
      datasets,
    }
  })

  // 最大合計値の計算
  const calculateMaxTotal = () => {
    const data = processedData.value
    const keys = Object.keys(data)
    const categories = Object.keys(props.colorMapping)

    const totals = keys.map(key => {
      return categories.reduce((sum, category) => {
        // _displayNameはデータプロパティではないのでスキップ
        if (category === '_displayName') return sum
        return sum + (data[key][category] || 0)
      }, 0)
    })

    return Math.max(...totals, 0) // 空の場合は0を返す
  }

  // チャートスケールの計算
  const chartMaxScale = computed(() => {
    const maxTotal = calculateMaxTotal()
    // 5単位で切り上げ、最小スケール以上を保証
    return Math.max(props.minScale, Math.ceil(maxTotal / 5) * 5)
  })

  // チャートの初期化
  const initChart = () => {
    if (!chartCanvas.value) return

    const ctx = chartCanvas.value.getContext('2d')

    // 既存のチャートがあれば破棄
    if (chart) {
      chart.destroy()
    }

    // チャート設定
    chart = new Chart(ctx, {
      type: 'bar',
      data: chartData.value,
      options: {
        indexAxis: 'y', // 横向きバーチャート
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false, // カスタム凡例を使用
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: function (context) {
                const label = context.dataset.label || ''
                const value = context.parsed.x
                return `${label}: ${value}`
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            suggestedMax: chartMaxScale.value,
            ticks: {
              stepSize: chartMaxScale.value > 10 ? 5 : 2,
            },
            title: {
              display: false, // X軸タイトルを非表示
            },
          },
          y: {
            stacked: true,
            title: {
              display: false, // Y軸タイトルを非表示
            },
          },
        },
        layout: {
          padding: {
            left: 10,
            right: 20,
            top: 0,
            bottom: 0,
          },
        },
      },
    })
  }

  // データが変わったらチャートを更新
  watch(
    () => props.data,
    () => {
      initChart()
    },
    { deep: true }
  )

  // コンポーネントがマウントされたらチャートを初期化
  onMounted(() => {
    initChart()

    // ウィンドウのリサイズ時にチャートをリサイズ
    window.addEventListener('resize', initChart)
  })

  // コンポーネントがアンマウントされる前にイベントリスナーをクリーンアップ
  onBeforeUnmount(() => {
    window.removeEventListener('resize', initChart)
    if (chart) {
      chart.destroy()
      chart = null
    }
  })
</script>

<style lang="less" scoped>
  .chart-wrapper {
    width: 100%;

    .legend-container {
      display: flex;
      justify-content: center;
      gap: 15px;
      margin: 15px 0;
      flex-wrap: wrap;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 5px;

        .legend-color {
          width: 15px;
          height: 15px;
          border-radius: 4px;
        }
        span {
          font-size: 12px;
          color: #333;
        }
      }
    }

    .chart-container {
      position: relative;
      // height: v-bind('chartHeight');
      margin: 20px 0;
    }
  }
</style>
